/**
 * shape-builders — turns declarative ShapePlan objects into Office.js proxy
 * calls. Shared by the visual primitives (add_shape, add_line, insert_image)
 * and the compose_slide layout engine (PPT2), so shape-construction logic
 * exists exactly once.
 *
 * Builders NEVER call PowerPoint.run themselves — the caller owns the
 * run/load/sync lifecycle (and the serialized execution chain). All geometry
 * is in points on the 960×540 (16:9) canvas.
 *
 * Requirement sets: text boxes, geometric shapes, lines, fills, line format,
 * fonts, paragraph alignment, text-frame options are PowerPointApi 1.4;
 * image fill (ShapeFill.setImage) is 1.8 — callers gate accordingly.
 */

// ---------------------------------------------------------------------------
// Plan types (pure data — also used by the Office-free layout engine)
// ---------------------------------------------------------------------------

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface FontSpec {
  /** Font family, e.g. "Segoe UI". */
  name?: string;
  /** Size in points. */
  size?: number;
  /** Hex "#RRGGBB" or named HTML colour. */
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface FillSpec {
  /** Hex "#RRGGBB" or named HTML colour. */
  color: string;
  /** 0.0 (opaque) – 1.0 (clear). */
  transparency?: number;
}

export interface LineSpec {
  color?: string;
  /** Weight in points. */
  weight?: number;
  dashStyle?: string;
}

export type HorizontalAlign = "left" | "center" | "right";
export type VerticalAlign = "top" | "middle" | "bottom";

export interface TextOptions {
  font?: FontSpec;
  align?: HorizontalAlign;
  verticalAlign?: VerticalAlign;
  /** Shrink text on overflow instead of spilling out of the shape. */
  autofit?: boolean;
  wordWrap?: boolean;
  /** Render paragraphs as a REAL PowerPoint bullet list (hanging indents). */
  bulletList?: boolean;
}

export type ShapePlan =
  | ({ kind: "textbox"; rect: Rect; text: string; fill?: FillSpec } & TextOptions)
  | ({ kind: "shape"; shapeType: GeometricShapeName; rect: Rect; fill?: FillSpec; line?: LineSpec; text?: string } & TextOptions)
  | { kind: "line"; rect: Rect; color?: string; weight?: number }
  | { kind: "image"; rect: Rect; base64: string };

/**
 * The geometric shape types we expose. Values mirror the
 * PowerPoint.GeometricShapeType enum literals — validated here so a typo from
 * the model becomes a clear error instead of an Office.js InvalidArgument.
 */
export const GEOMETRIC_SHAPE_TYPES = [
  "rectangle",
  "roundedRectangle",
  "ellipse",
  "triangle",
  "rightTriangle",
  "diamond",
  "pentagon",
  "hexagon",
  "chevron",
  "rightArrow",
  "leftArrow",
  "upArrow",
  "downArrow",
  "star5",
  "heart",
  "lightningBolt",
] as const;
export type GeometricShapeName = (typeof GEOMETRIC_SHAPE_TYPES)[number];

export function isGeometricShapeName(value: unknown): value is GeometricShapeName {
  return typeof value === "string" && (GEOMETRIC_SHAPE_TYPES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Enum mapping helpers
// ---------------------------------------------------------------------------

const HORIZONTAL_ALIGN_MAP: Record<HorizontalAlign, string> = {
  left: "Left",
  center: "Center",
  right: "Right",
};

const VERTICAL_ALIGN_MAP: Record<VerticalAlign, string> = {
  top: "Top",
  middle: "Middle",
  bottom: "Bottom",
};

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/** Apply font/paragraph/frame text options to a shape that HAS a text frame. */
function applyTextOptions(shape: PowerPoint.Shape, options: TextOptions): void {
  const { font, align, verticalAlign, autofit, wordWrap, bulletList } = options;
  if (bulletList !== undefined) {
    shape.textFrame.textRange.paragraphFormat.bulletFormat.visible = bulletList;
  }
  if (font) {
    const f = shape.textFrame.textRange.font;
    if (font.name !== undefined) f.name = font.name;
    if (font.size !== undefined) f.size = font.size;
    if (font.color !== undefined) f.color = font.color;
    if (font.bold !== undefined) f.bold = font.bold;
    if (font.italic !== undefined) f.italic = font.italic;
    if (font.underline !== undefined) {
      f.underline = (font.underline
        ? "Single"
        : "None") as unknown as PowerPoint.ShapeFontUnderlineStyle;
    }
  }
  if (align) {
    shape.textFrame.textRange.paragraphFormat.horizontalAlignment =
      HORIZONTAL_ALIGN_MAP[align] as unknown as PowerPoint.ParagraphHorizontalAlignment;
  }
  if (verticalAlign) {
    shape.textFrame.verticalAlignment =
      VERTICAL_ALIGN_MAP[verticalAlign] as unknown as PowerPoint.TextVerticalAlignment;
  }
  if (autofit !== undefined) {
    shape.textFrame.autoSizeSetting = (autofit
      ? "AutoSizeTextToFitShape"
      : "AutoSizeNone") as unknown as PowerPoint.ShapeAutoSize;
  }
  if (wordWrap !== undefined) {
    shape.textFrame.wordWrap = wordWrap;
  }
}

function applyFill(shape: PowerPoint.Shape, fill: FillSpec): void {
  shape.fill.setSolidColor(fill.color);
  if (fill.transparency !== undefined) {
    shape.fill.transparency = fill.transparency;
  }
}

function applyLineFormat(shape: PowerPoint.Shape, line: LineSpec): void {
  if (line.color !== undefined) shape.lineFormat.color = line.color;
  if (line.weight !== undefined) shape.lineFormat.weight = line.weight;
  if (line.dashStyle !== undefined) {
    shape.lineFormat.dashStyle = line.dashStyle as unknown as PowerPoint.ShapeLineDashStyle;
  }
}

/** Anything that owns a shape collection: a slide, a layout, or the MASTER. */
export interface ShapeSurface {
  shapes: PowerPoint.ShapeCollection;
}

/**
 * Render one ShapePlan onto a shape surface (slide, layout, or slide master —
 * SlideMaster.shapes is the same mutable ShapeCollection per the typings).
 * Returns the created shape proxy so the caller can load ids after its own
 * context.sync().
 */
export function buildShape(slide: ShapeSurface, plan: ShapePlan): PowerPoint.Shape {
  switch (plan.kind) {
    case "textbox": {
      const shape = slide.shapes.addTextBox(plan.text, {
        left: plan.rect.left,
        top: plan.rect.top,
        width: plan.rect.width,
        height: plan.rect.height,
      });
      if (plan.fill) applyFill(shape, plan.fill);
      applyTextOptions(shape, plan);
      return shape;
    }

    case "shape": {
      const shape = slide.shapes.addGeometricShape(
        plan.shapeType as unknown as PowerPoint.GeometricShapeType,
        {
          left: plan.rect.left,
          top: plan.rect.top,
          width: plan.rect.width,
          height: plan.rect.height,
        }
      );
      if (plan.fill) applyFill(shape, plan.fill);
      if (plan.line) applyLineFormat(shape, plan.line);
      if (plan.text) {
        shape.textFrame.textRange.text = plan.text;
        applyTextOptions(shape, plan);
      }
      return shape;
    }

    case "line": {
      // A straight connector drawn corner-to-corner of its bounding rect.
      const shape = slide.shapes.addLine(
        "Straight" as unknown as PowerPoint.ConnectorType,
        {
          left: plan.rect.left,
          top: plan.rect.top,
          width: plan.rect.width,
          height: plan.rect.height,
        }
      );
      applyLineFormat(shape, { color: plan.color, weight: plan.weight });
      return shape;
    }

    case "image": {
      // PowerPointApi 1.8: a rectangle filled with the image. Deterministic
      // geometry (unlike the selection-dependent Common-API insert) and it
      // composes inside the caller's single PowerPoint.run. Note fill.setImage
      // stretches to the rect — callers pre-fit the rect for aspect ratio.
      const shape = slide.shapes.addGeometricShape(
        "Rectangle" as unknown as PowerPoint.GeometricShapeType,
        {
          left: plan.rect.left,
          top: plan.rect.top,
          width: plan.rect.width,
          height: plan.rect.height,
        }
      );
      shape.fill.setImage(plan.base64);
      shape.lineFormat.visible = false;
      return shape;
    }
  }
}

// ---------------------------------------------------------------------------
// Image-fill verification
// ---------------------------------------------------------------------------

/**
 * Verify that picture fills actually applied — `fill.setImage` can silently
 * not stick (the batch is accepted but the fill never renders; observed live
 * with a deck-wide logo: the shapes existed, the picture didn't). Loads each
 * shape's fill type, retries missing ones ONCE with an isolated sync, and
 * returns counts so callers can surface failures instead of hiding them.
 *
 * Caller owns the PowerPoint.run; `items` are shapes built with kind "image".
 */
export async function ensureImageFills(
  context: PowerPoint.RequestContext,
  items: Array<{ shape: PowerPoint.Shape; base64: string }>
): Promise<{ applied: number; retried: number; failed: number }> {
  if (items.length === 0) return { applied: 0, retried: 0, failed: 0 };

  const isPicture = (type: unknown) =>
    typeof type === "string" && type.toLowerCase().includes("picture");

  items.forEach(({ shape }) => shape.fill.load("type"));
  await context.sync();

  const missing = items.filter(({ shape }) => !isPicture(shape.fill.type));
  if (missing.length === 0) return { applied: items.length, retried: 0, failed: 0 };

  // Retry each missing fill with its own isolated sync (smaller batches).
  for (const { shape, base64 } of missing) {
    shape.fill.setImage(base64);
    await context.sync();
  }
  missing.forEach(({ shape }) => shape.fill.load("type"));
  await context.sync();

  const failed = missing.filter(({ shape }) => !isPicture(shape.fill.type)).length;
  return {
    applied: items.length - failed,
    retried: missing.length,
    failed,
  };
}

// ---------------------------------------------------------------------------
// Shared shape resolution (extracted from the previously-triplicated logic)
// ---------------------------------------------------------------------------

/**
 * Resolve a shape on a slide by stable id (preferred) or index. Caller must
 * have loaded `shapes` with at least "items/id" and synced. Throws a clear
 * error the AI can act on.
 */
export function resolveShape(
  shapes: PowerPoint.ShapeCollection,
  ref: { shapeId?: unknown; shapeIndex?: unknown }
): PowerPoint.Shape {
  if (typeof ref.shapeId === "string" && ref.shapeId.length > 0) {
    const match = shapes.items.find((s) => s.id === ref.shapeId);
    if (!match) {
      throw new Error(
        `No shape with id "${ref.shapeId}" on this slide — call get_slide to list current shape ids.`
      );
    }
    return match;
  }
  if (typeof ref.shapeIndex === "number") {
    if (ref.shapeIndex < 0 || ref.shapeIndex >= shapes.items.length) {
      throw new Error(
        `shapeIndex ${ref.shapeIndex} is out of range (slide has ${shapes.items.length} shapes).`
      );
    }
    return shapes.items[ref.shapeIndex];
  }
  throw new Error("Provide shapeId (preferred, stable) or shapeIndex.");
}

/** Pull the optional font params (fontName/fontSize/fontColor/bold/italic) into a FontSpec. */
export function fontSpecFromArgs(args: Record<string, unknown>): FontSpec | undefined {
  const spec: FontSpec = {};
  if (typeof args.fontName === "string") spec.name = args.fontName;
  if (typeof args.fontSize === "number") spec.size = args.fontSize;
  if (typeof args.fontColor === "string") spec.color = args.fontColor;
  if (typeof args.bold === "boolean") spec.bold = args.bold;
  if (typeof args.italic === "boolean") spec.italic = args.italic;
  return Object.keys(spec).length > 0 ? spec : undefined;
}

/**
 * Parse a geometric-shape spec (shapeType + left/top/width/height plus optional
 * fill/line/text/font) into a "shape" ShapePlan. Shared by the slide `add_shape`
 * tool and the batch master/layout geometry tools so shape-arg parsing lives
 * exactly once. Throws a clear, model-actionable error on an unknown shapeType.
 */
export function shapePlanFromArgs(
  args: Record<string, unknown>
): Extract<ShapePlan, { kind: "shape" }> {
  if (!isGeometricShapeName(args.shapeType)) {
    throw new Error(
      `Unknown shapeType "${String(args.shapeType)}". Valid types: ${GEOMETRIC_SHAPE_TYPES.join(", ")}.`
    );
  }
  return {
    kind: "shape",
    shapeType: args.shapeType,
    rect: {
      left: args.left as number,
      top: args.top as number,
      width: args.width as number,
      height: args.height as number,
    },
    fill:
      typeof args.fillColor === "string"
        ? {
            color: args.fillColor,
            transparency:
              typeof args.fillTransparency === "number" ? args.fillTransparency : undefined,
          }
        : undefined,
    line:
      typeof args.lineColor === "string" || typeof args.lineWeight === "number"
        ? {
            color: typeof args.lineColor === "string" ? args.lineColor : undefined,
            weight: typeof args.lineWeight === "number" ? args.lineWeight : undefined,
          }
        : undefined,
    text: typeof args.text === "string" ? args.text : undefined,
    font: fontSpecFromArgs(args),
  };
}

/**
 * Resolve a slide layout on a master by name (case-insensitive, preferred) or
 * 0-based index — mirrors resolveShape. Caller must have loaded `master.layouts`
 * with at least "items/name" and synced. Throws a clear error listing the
 * available layout names so the model can retry with a valid target.
 */
export function resolveLayout(
  master: PowerPoint.SlideMaster,
  ref: { layoutName?: unknown; layoutIndex?: unknown }
): PowerPoint.SlideLayout {
  const layouts = master.layouts.items;
  if (typeof ref.layoutName === "string" && ref.layoutName.length > 0) {
    const needle = ref.layoutName.toLowerCase();
    const match = layouts.find((l) => l.name.toLowerCase() === needle);
    if (!match) {
      throw new Error(
        `No layout named "${ref.layoutName}". Available: ${layouts
          .map((l) => l.name)
          .join(", ")} — call get_master_info to list layouts.`
      );
    }
    return match;
  }
  if (typeof ref.layoutIndex === "number") {
    if (ref.layoutIndex < 0 || ref.layoutIndex >= layouts.length) {
      throw new Error(
        `layoutIndex ${ref.layoutIndex} is out of range (master has ${layouts.length} layouts).`
      );
    }
    return layouts[ref.layoutIndex];
  }
  throw new Error("Provide layoutName (preferred) or layoutIndex to target a layout.");
}

// ---------------------------------------------------------------------------
// Image measurement (task-pane side, no Office.js)
// ---------------------------------------------------------------------------

/**
 * Fit an image of natural size (w×h) into a target rect. "contain" preserves
 * aspect ratio (letterboxing inside the rect, centred); "stretch" returns the
 * rect unchanged. Pure — unit-testable.
 */
export function fitRect(
  natural: { width: number; height: number },
  target: Rect,
  fit: "contain" | "stretch"
): Rect {
  if (fit === "stretch" || natural.width <= 0 || natural.height <= 0) return target;
  const scale = Math.min(target.width / natural.width, target.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;
  return {
    left: target.left + (target.width - width) / 2,
    top: target.top + (target.height - height) / 2,
    width,
    height,
  };
}

/** Fetch a URL into a data-URL base64 string (task-pane side; CORS applies). */
export async function fetchImageAsDataUrl(url: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(url);
  } catch (err) {
    // Deliberately instructive: the model CAN complete this task — generated /
    // chat files (e.g. usable.dev/files/…) are not CORS-readable from the task
    // pane, but the base64 path works. Tell it the exact recovery move instead
    // of letting it give up and ask the user to download the file manually.
    throw new Error(
      `Could not fetch the image URL from the add-in (CORS/network): ${String(err)}. ` +
        `Do NOT ask the user to download it manually — instead retrieve the image bytes ` +
        `yourself (e.g. read the generated file with your file tools) and call this tool ` +
        `again with the base64 parameter. Any browser-decodable format is accepted and ` +
        `normalized to PNG automatically.`
    );
  }
  if (!response.ok) {
    throw new Error(
      `Image URL returned HTTP ${response.status}. If the file is access-protected, ` +
        `retrieve its bytes with your own tools and pass base64 instead.`
    );
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read fetched image data."));
    reader.readAsDataURL(blob);
  });
}

/**
 * Measure a base64 PNG/JPEG's natural pixel size using an Image element
 * (cheap, no Office.js). Resolves null when measurement fails (e.g. corrupt
 * data) — callers then fall back to "stretch".
 */
export function measureImage(
  base64: string,
  mimeType = "image/png"
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = base64.startsWith("data:") ? base64 : `data:${mimeType};base64,${base64}`;
    } catch {
      resolve(null);
    }
  });
}

/** Pure: scale (w,h) down so the longest edge is ≤ maxDimension (never up). */
export function computeScaledSize(
  natural: { width: number; height: number },
  maxDimension: number
): { width: number; height: number } {
  const longest = Math.max(natural.width, natural.height);
  if (longest <= maxDimension || longest <= 0) return { ...natural };
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(natural.width * scale)),
    height: Math.max(1, Math.round(natural.height * scale)),
  };
}

/**
 * Normalize ANY incoming image (PNG/JPEG/WebP/GIF, raw base64 or data-URL,
 * with or without line-wrapping) into clean PNG base64 via a canvas round-trip,
 * downscaled so the longest edge is ≤ maxDimension.
 *
 * WHY: PowerPoint's fill.setImage renders "The picture can't be displayed"
 * for payloads the browser happily decodes — WebP (common for AI-generated
 * images), base64 with embedded newlines, or oversized data. The browser is
 * the tolerant decoder, so decode here and hand Office.js a format it is
 * guaranteed to accept. Resolves null when the data genuinely can't be
 * decoded — callers must surface a clear error instead of inserting garbage.
 */
export function normalizeImageToPng(
  base64OrDataUrl: string,
  maxDimension = 1920
): Promise<{ base64: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    try {
      // Line-wrapped base64 breaks both data-URL parsing and Office.js.
      const compact = base64OrDataUrl.replace(/\s+/g, "");
      const dataUrl = compact.startsWith("data:") ? compact : `data:image/png;base64,${compact}`;

      const img = new Image();
      img.onload = () => {
        try {
          const natural = { width: img.naturalWidth, height: img.naturalHeight };
          if (natural.width <= 0 || natural.height <= 0) {
            resolve(null);
            return;
          }
          const size = computeScaledSize(natural, maxDimension);
          const canvas = document.createElement("canvas");
          canvas.width = size.width;
          canvas.height = size.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0, size.width, size.height);
          const pngBase64 = canvas.toDataURL("image/png").replace(/^data:[^,]*,/, "");
          resolve({ base64: pngBase64, width: size.width, height: size.height });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch {
      resolve(null);
    }
  });
}
